/** @jsx jsx */
import { jsx } from '@emotion/core'
import style from './payStyle';
import React from 'react';

import { SiteLink, usePost, useRouter, Button, ErrorMessage } from '../../../components';
import env from '../../../env';
import { useForm } from '../../../form';
import { useStore } from '../../../store';

import { CartSummary } from '../cartSummary/summary';
import { getEwayFormModel, digitsOnly, toTwoDigitYear } from './ewayFormModel';

const eCryptScriptUrl = 'https://secure.ewaypayments.com/scripts/eCrypt.min.js';

// loads eWay client side encryption (window.eCrypt) only when the payment step is used
function loadECrypt() {
  if (window.eCrypt) return Promise.resolve(window.eCrypt);
  return new Promise((resolve, reject) => {
    let script = document.querySelector(`script[src="${eCryptScriptUrl}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = eCryptScriptUrl;
      script.async = true;
      document.body.appendChild(script);
    }
    script.addEventListener('load', () => window.eCrypt ? resolve(window.eCrypt) : reject());
    script.addEventListener('error', () => reject());
  });
}

export function PayByEway({ cart }) {
  const [eCryptError, setECryptError] = React.useState(null);

  const post = usePost();
  const { Redirect } = useRouter();
  const { dispatch } = useStore();

  const form = useForm(getEwayFormModel({}), { usePlaceholder: true });
  const render = (name) => form.renderControl(name, null);

  React.useEffect(() => {
    loadECrypt().catch(() => setECryptError('The payment system could not be loaded. Please refresh the page and try again.'));
  }, []);

  function payNow(e) {
    form.validateForm(e, () => {
      if (!window.eCrypt || !env.ewayClientKey) {
        setECryptError('The payment system is not ready. Please refresh the page and try again.');
        return;
      }
      setECryptError(null);
      const value = form.getValue();
      const encrypt = (text) => window.eCrypt.encryptValue(text, env.ewayClientKey);
      const payment = {
        cartId: env.getDevCartId(),
        itemTotalAmount: cart.itemAmount,
        totalAmount: cart.totalAmount,
        cardName: value.cardHolderName.trim(),
        cardNumber: encrypt(digitsOnly(value.cardNumber)),
        cardExpMM: digitsOnly(value.cardExpiryMM).padStart(2, '0'),
        cardExpYY: toTwoDigitYear(value.cardExpiryYY),
        cvv: encrypt(digitsOnly(value.securityCode)),
      };
      post.send(env.apiBase + '/api/cart/payEway', payment);
    });
  }

  if (post.done()) {
    env.setDevCartId(null);
    dispatch({ type: 'SET_STATE', payload: { cartInfo: 0 } });
    return <Redirect to={`/order-thankyou?o=${post.response.results.OrderIdToken}`} />
  }

  return <div css={style.pay}>
    <div className="row" >
      <div className="col-md-9">
        <h3>Pay by Credit Card</h3>

        <div css={style.ewayWrapper}>
          <div className="row">
            <div className="col-12">{render('cardHolderName')}</div>
            <div className="col-12">{render('cardNumber')}</div>
          </div>
          <div className="row">
            <div className="col-12" css={style.ewayLabel}>Expiry</div>
            <div className="col-4">{render('cardExpiryMM')}</div>
            <div className="col-4">{render('cardExpiryYY')}</div>
            <div className="col-4">{render('securityCode')}</div>
          </div>

          <div css={style.ewayInfoText}>
            Payment through encrypted gateway can be made by Visa, Mastercard or AMEX. Please enter your credit card details to complete your order.
            For alternative payment methods please <SiteLink to="#contactForm" className="contact-link">contact us</SiteLink>.
          </div>
        </div>

      </div>

      <div className="col-md-3">
        <CartSummary cart={cart} />
      </div>
    </div>
    <div css={style.formActions}>
      <Button onClick={payNow} status={post.status}>Pay Now</Button>
      <ErrorMessage errors={form.errors} summaryMessage="Please review the card details." />
      <ErrorMessage errors={eCryptError} />
      <ErrorMessage errors={post.errors} />
      <div css={style.payeeText}>Payment will be made to Lyndaw Park Pty Ltd</div>
    </div>
  </div>
}
